import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { Icon } from 'src/components/general/icon';
import {
  CardBaseContent,
  DashboardCard,
} from 'src/components/pages/dashboard/components/shared';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const CardContentContainer = styled(CardBaseContent)`
  align-items: center;
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: 180px;
  justify-content: center;
  padding: 0px 20px;

  ${up('md')} {
    height: 311px;
  }
`;

const UnassignedContactIcon = styled(Icon)`
  svg {
    height: 83px;
    width: 83px;
  }
`;

const MessageText = styled.div`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  font-weight: 400;
  letter-spacing: -0.45px;
  line-height: normal;
  max-width: 200px;
  text-align: center;
`;

const StrongTextLink = styled(Link)`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.45px;
  line-height: normal;

  &:hover {
    color: ${({ theme }) => theme.colors.gogo};
  }
`;

export const GET_UNASSIGNED_CONTACTS_QUERY = gql`
  query GetUnassignedContacts($institutionId: Int!, $where: ContactWhereInput) {
    contacts(institutionId: $institutionId, where: $where) {
      totalCount
    }
  }
`;

export const UnassignedContactsCard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();

  const { data, loading } = useQuery(GET_UNASSIGNED_CONTACTS_QUERY, {
    variables: {
      institutionId: currentInstitution.id,
      where: { assignee: { _eq: null } },
    },
    onError: handleGraphQLError,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const queryParams = useMemo(() => {
    const params = {
      institution_id: currentInstitution.id,
      institution_type: 'company',
      'view-code': 'custom',
      'q[advanced][assigned_unassigned]': '1',
    };

    return Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  }, [currentInstitution.id]);

  const total = (data?.contacts?.totalCount ?? 0) as number;
  const isEmpty = !loading && total === 0;
  const testId = 'unassigned-contacts-card';
  const viewLink = `/i/company/${currentInstitution.id}/contacts?${queryParams}`;
  const cardTitle = t('dashboard:cards.unassignedContacts.title');

  return (
    <DashboardCard loading={loading} data-lgg-id={testId}>
      <DashboardCard.Header
        testId={`${testId}-header`}
        title={cardTitle}
        cardInfoI18nKey="dashboard:cards.unassignedContacts.tooltipDescription"
        cardTextTitle={cardTitle}
      />
      <DashboardCard.Content data-lgg-id={`${testId}-content`}>
        <CardContentContainer $isEmpty={isEmpty}>
          {isEmpty ? (
            <DashboardCard.EmptyState
              testId={`${testId}-empty`}
              icon="contact"
              title={t('dashboard:cards.unassignedContacts.emptyState.title')}
            />
          ) : (
            <>
              <UnassignedContactIcon type="unassignedContact" />
              <MessageText>
                <Trans
                  i18nKey="dashboard:cards.unassignedContacts.message"
                  values={{
                    value: total.toLocaleString(),
                  }}
                  components={{
                    strong: <StrongTextLink to={viewLink} />,
                  }}
                />
              </MessageText>
            </>
          )}
        </CardContentContainer>
      </DashboardCard.Content>
      {!isEmpty && (
        <DashboardCard.Footer
          testId={`${testId}-footer`}
          linkTo={viewLink}
          linkText={t('common:viewNext')}
        />
      )}
    </DashboardCard>
  );
};
